// Advanced Reporting & Analytics — pure aggregation helpers for the admin
// portal's Reports tab (views/mainViews/DashboardViews/Reports.vue).
//
// Deliberately framework- and Firebase-free: every function takes plain arrays
// of Firestore doc data and returns plain rows, so the same code can be run in
// Node against real exports to sanity-check numbers. READ-ONLY by design —
// nothing here writes anywhere.
//
// Data conventions this relies on (cart/BNPL checkout, see RheeGold/functions
// prepareCartOrder / prepareExhibitorOrder):
//   - orders.registrationType === 'exhibitor' for exhibitor checkouts; absent
//     means an attendee cart.
//   - status: 'paid' | 'partial' (charged, but some attendee provisioning
//     failed — money WAS collected) | 'failed' | 'pending_bnpl' | 'pending'.
//   - Attendee slots pair 1:1 with productType 'pass' items expanded by
//     quantity (pairAttendeesWithPassItems), so pass quantity == registrations.
//   - totalCents = subtotalCents - couponDiscountCents + feeCents.
//   - item.dataSet null/absent falls into the org's DEFAULT dataSet bucket,
//     matching the platform-wide `in [id, null]` convention (and the mobile
//     apps' `isDefault ?: true` parsing of account/appInformation.dataSet).

export const PAID_STATUSES = ['paid', 'partial'];

export function toMillis(ts) {
	if (!ts) return 0;
	if (typeof ts.toMillis === 'function') return ts.toMillis();
	if (typeof ts.seconds === 'number') return ts.seconds * 1000;
	if (typeof ts._seconds === 'number') return ts._seconds * 1000;
	const d = new Date(ts);
	return isNaN(d.getTime()) ? 0 : d.getTime();
}

export function isPaid(order) {
	return PAID_STATUSES.indexOf(order.status) !== -1;
}

export function isExhibitorOrder(order) {
	return order.registrationType === 'exhibitor';
}

// dataSets: the store's getDataSets shape ([{ value, name, raw }]) or null for
// orgs without the multi-dataSet concept (DEC/DTS/DTW/Toronto).
export function buildDataSetHelper(dataSets) {
	const list = Array.isArray(dataSets) ? dataSets : [];
	const defaultEntry = list.find((d) => !d.raw || d.raw.isDefault !== false) || list[0] || null;
	const defaultId = defaultEntry ? defaultEntry.value : null;
	const names = {};
	list.forEach((d) => {
		names[d.value] = d.name || d.value;
	});
	return {
		hasDataSets: list.length > 0,
		defaultId,
		options: list.map((d) => ({ value: d.value, text: d.name || d.value })),
		resolve(id) {
			if (id === null || id === undefined || id === '') return defaultId;
			return id;
		},
		label(id) {
			const resolved = this.resolve(id);
			if (resolved === null) return 'All';
			return names[resolved] || resolved;
		},
	};
}

function itemMatches(item, dsId, ds) {
	if (!dsId) return true;
	return ds.resolve(item && item.dataSet) === dsId;
}

export function orderMatchesDataSet(order, dsId, ds) {
	if (!dsId) return true;
	return (order.items || []).some((i) => itemMatches(i, dsId, ds));
}

// Fraction of an order attributable to the selected dataSet, used to prorate
// order-level amounts (coupon discount, fee, total) in mixed-event carts.
// Exactly 1 when no dataSet filter is active.
export function orderShare(order, dsId, ds) {
	if (!dsId) return 1;
	const items = order.items || [];
	if (!items.length) return 0;
	let all = 0;
	let matched = 0;
	items.forEach((i) => {
		const lt = Number(i.lineTotal) || 0;
		all += lt;
		if (itemMatches(i, dsId, ds)) matched += lt;
	});
	if (all > 0) return matched / all;
	const n = items.filter((i) => itemMatches(i, dsId, ds)).length;
	return n / items.length;
}

// Date range + test-mode filter. fromMs/toMs inclusive, either may be null.
export function filterOrders(orders, { fromMs = null, toMs = null, includeTest = false } = {}) {
	return (orders || []).filter((o) => {
		if (!includeTest && o.isTestMode === true) return false;
		const t = toMillis(o.createdAt);
		if (fromMs !== null && t < fromMs) return false;
		if (toMs !== null && t > toMs) return false;
		return true;
	});
}

// Registrations an order contributes: attendee pass seats, or 1 per exhibitor
// checkout (one business/booth registration).
export function registrationsForOrder(order, dsId, ds) {
	if (isExhibitorOrder(order)) return orderMatchesDataSet(order, dsId, ds) ? 1 : 0;
	return (order.items || [])
		.filter((i) => i.productType === 'pass' && itemMatches(i, dsId, ds))
		.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
}

// 1. Summary KPIs (paid + partial orders only).
export function summarize(orders, dsId, ds) {
	const out = {
		grossCents: 0,
		discountCents: 0,
		feeCents: 0,
		collectedCents: 0,
		netCents: 0,
		paidOrders: 0,
		attendeeOrders: 0,
		exhibitorOrders: 0,
		attendeeRegistrations: 0,
		exhibitorRegistrations: 0,
		attendeeCollectedCents: 0,
		exhibitorCollectedCents: 0,
	};
	orders.forEach((o) => {
		if (!isPaid(o) || !orderMatchesDataSet(o, dsId, ds)) return;
		const share = orderShare(o, dsId, ds);
		const collected = (Number(o.totalCents) || 0) * share;
		out.grossCents += (Number(o.subtotalCents) || 0) * share;
		out.discountCents += (Number(o.couponDiscountCents) || 0) * share;
		out.feeCents += (Number(o.feeCents) || 0) * share;
		out.collectedCents += collected;
		out.paidOrders += 1;
		if (isExhibitorOrder(o)) {
			out.exhibitorOrders += 1;
			out.exhibitorRegistrations += 1;
			out.exhibitorCollectedCents += collected;
		} else {
			out.attendeeOrders += 1;
			out.attendeeRegistrations += registrationsForOrder(o, dsId, ds);
			out.attendeeCollectedCents += collected;
		}
	});
	out.netCents = out.grossCents - out.discountCents;
	Object.keys(out).forEach((k) => {
		if (/Cents$/.test(k)) out[k] = Math.round(out[k]);
	});
	return out;
}

// 2. Registrations & revenue by product (paid + partial orders).
// catalog: { products: [...], exhibitorProducts: [...] } — used for zero rows.
export function productRows(orders, dsId, ds, catalog, includeZero) {
	const rows = {};
	const keyFor = (type, productId, title) => `${type}|${productId || title}`;
	const ensure = (type, productId, title, dataSet, productType) => {
		const key = keyFor(type, productId, title);
		if (!rows[key]) {
			rows[key] = {
				type,
				productId: productId || '',
				productTitle: title || '(untitled product)',
				productType: productType || '',
				eventId: ds.resolve(dataSet) || '',
				event: ds.label(dataSet),
				units: 0,
				orders: 0,
				grossCents: 0,
			};
		}
		return rows[key];
	};
	orders.forEach((o) => {
		if (!isPaid(o)) return;
		const type = isExhibitorOrder(o) ? 'Exhibitor' : 'Attendee';
		const seen = {};
		(o.items || []).forEach((i) => {
			if (!itemMatches(i, dsId, ds)) return;
			const row = ensure(type, i.productId, i.productTitle, i.dataSet, i.exhibitorProductType || i.productType);
			row.units += Number(i.quantity) || 0;
			row.grossCents += Number(i.lineTotal) || 0;
			const k = keyFor(type, i.productId, i.productTitle);
			if (!seen[k]) {
				row.orders += 1;
				seen[k] = true;
			}
		});
	});
	if (includeZero && catalog) {
		const add = (list, type) =>
			(list || []).forEach((p) => {
				if (!itemMatches(p, dsId, ds)) return;
				ensure(type, p.productId, p.productTitle, p.dataSet, p.exhibitorProductType || p.productType);
			});
		add(catalog.products, 'Attendee');
		add(catalog.exhibitorProducts, 'Exhibitor');
	}
	return Object.values(rows).sort(
		(a, b) => b.grossCents - a.grossCents || b.units - a.units || a.productTitle.localeCompare(b.productTitle)
	);
}

function pad(n) {
	return n < 10 ? `0${n}` : `${n}`;
}
function dayKey(d) {
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startOfBucket(ms, granularity) {
	const d = new Date(ms);
	d.setHours(0, 0, 0, 0);
	if (granularity === 'week') {
		const dow = (d.getDay() + 6) % 7; // Monday = 0
		d.setDate(d.getDate() - dow);
	} else if (granularity === 'month') {
		d.setDate(1);
	}
	return d;
}
function nextBucket(d, granularity) {
	const n = new Date(d.getTime());
	if (granularity === 'week') n.setDate(n.getDate() + 7);
	else if (granularity === 'month') n.setMonth(n.getMonth() + 1);
	else n.setDate(n.getDate() + 1);
	return n;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Chart axis: "Jul 27" (day/week) or "Jul 2026" (month).
function shortPeriodLabel(d, granularity) {
	if (granularity === 'month') return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
	return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}
// Table: "Oct 12, 2026", "Week of Oct 12, 2026", "October 2026".
function longPeriodLabel(d, granularity) {
	if (granularity === 'month') return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
	const date = `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
	return granularity === 'week' ? `Week of ${date}` : date;
}

// 3. Registrations over time (paid + partial), gap-filled between first and
// last bucket so the chart's x-axis is continuous.
export function overTimeRows(orders, dsId, ds, granularity = 'day') {
	const buckets = {};
	let minMs = null;
	let maxMs = null;
	orders.forEach((o) => {
		if (!isPaid(o) || !orderMatchesDataSet(o, dsId, ds)) return;
		const t = toMillis(o.createdAt);
		if (!t) return;
		const start = startOfBucket(t, granularity);
		const key = dayKey(start);
		if (!buckets[key]) {
			buckets[key] = { attendeeRegistrations: 0, exhibitorRegistrations: 0, orders: 0, collectedCents: 0 };
		}
		const b = buckets[key];
		const regs = registrationsForOrder(o, dsId, ds);
		if (isExhibitorOrder(o)) b.exhibitorRegistrations += regs;
		else b.attendeeRegistrations += regs;
		b.orders += 1;
		b.collectedCents += (Number(o.totalCents) || 0) * orderShare(o, dsId, ds);
		const s = start.getTime();
		if (minMs === null || s < minMs) minMs = s;
		if (maxMs === null || s > maxMs) maxMs = s;
	});
	if (minMs === null) return [];
	const rows = [];
	let cursor = new Date(minMs);
	let guard = 0;
	let cumulative = 0;
	while (cursor.getTime() <= maxMs && guard < 2000) {
		const key = dayKey(cursor);
		const b = buckets[key] || { attendeeRegistrations: 0, exhibitorRegistrations: 0, orders: 0, collectedCents: 0 };
		cumulative += b.attendeeRegistrations + b.exhibitorRegistrations;
		rows.push({
			period: key,
			label: granularity === 'week' ? `Wk of ${key}` : granularity === 'month' ? key.slice(0, 7) : key,
			shortLabel: shortPeriodLabel(cursor, granularity),
			periodLabel: longPeriodLabel(cursor, granularity),
			attendeeRegistrations: b.attendeeRegistrations,
			exhibitorRegistrations: b.exhibitorRegistrations,
			orders: b.orders,
			collectedCents: Math.round(b.collectedCents),
			cumulativeRegistrations: cumulative,
		});
		cursor = nextBucket(cursor, granularity);
		guard += 1;
	}
	return rows;
}

function couponIsActive(c, nowMs) {
	if (!c) return null;
	if (c.isValid === false || c.couponIsValid === false) return false;
	const exp = toMillis(c.expiration);
	if (exp && exp < nowMs) return false;
	return true;
}

// 4. Coupon usage (paid + partial). coupons = coupons collection docs.
export function couponRows(orders, dsId, ds, coupons, includeUnused, nowMs = Date.now()) {
	const byCode = {};
	(coupons || []).forEach((c) => {
		if (!c || !c.couponCode) return;
		const code = String(c.couponCode).trim().toLowerCase();
		if (!byCode[code]) byCode[code] = c;
	});
	const rows = {};
	const ensure = (code) => {
		if (!rows[code]) {
			const c = byCode[code];
			let value = '';
			if (c && c.couponPercent) value = `${c.couponPercent}%`;
			else if (c && c.couponAmount) value = `$${(c.couponAmount / 100).toFixed(2)}`;
			const active = couponIsActive(c, nowMs);
			rows[code] = {
				code,
				description: (c && c.description) || (c ? '' : '(coupon doc not found)'),
				couponType: (c && c.couponType) || (c ? 'any' : ''),
				value,
				status: active === null ? 'Unknown' : active ? 'Active' : 'Inactive',
				uses: 0,
				registrations: 0,
				discountCents: 0,
				collectedCents: 0,
			};
		}
		return rows[code];
	};
	orders.forEach((o) => {
		if (!isPaid(o) || !o.couponCode || !orderMatchesDataSet(o, dsId, ds)) return;
		const code = String(o.couponCode).trim().toLowerCase();
		const share = orderShare(o, dsId, ds);
		const row = ensure(code);
		row.uses += 1;
		row.registrations += registrationsForOrder(o, dsId, ds);
		row.discountCents += (Number(o.couponDiscountCents) || 0) * share;
		row.collectedCents += (Number(o.totalCents) || 0) * share;
	});
	if (includeUnused) Object.keys(byCode).forEach((code) => ensure(code));
	return Object.values(rows)
		.map((r) => Object.assign(r, { discountCents: Math.round(r.discountCents), collectedCents: Math.round(r.collectedCents) }))
		.sort((a, b) => b.uses - a.uses || b.discountCents - a.discountCents || a.code.localeCompare(b.code));
}

function personName(p) {
	if (!p) return '';
	return `${p.firstName || ''} ${p.lastName || ''}`.trim();
}

function itemSummary(order, dsId, ds) {
	return (order.items || [])
		.filter((i) => itemMatches(i, dsId, ds))
		.map((i) => `${i.quantity || 1}x ${i.productTitle}`)
		.join('; ');
}

// 5. Exhibitors — every exhibitor checkout, any status.
export function exhibitorRows(orders, dsId, ds) {
	return orders
		.filter((o) => isExhibitorOrder(o) && orderMatchesDataSet(o, dsId, ds))
		.map((o) => {
			const share = orderShare(o, dsId, ds);
			return {
				orderId: o.orderId,
				company:
					(o.business && o.business.businessName) || (o.buyer && o.buyer.businessName) || '(no company name)',
				package: o.mainProductType || (o.business && o.business.businessLevel) || '',
				items: itemSummary(o, dsId, ds),
				contacts: Array.isArray(o.contacts) ? o.contacts.length : 0,
				buyerName: personName(o.buyer),
				buyerEmail: (o.buyer && o.buyer.email) || '',
				couponCode: o.couponCode || '',
				subtotalCents: Math.round((Number(o.subtotalCents) || 0) * share),
				totalCents: Math.round((Number(o.totalCents) || 0) * share),
				createdAtMillis: toMillis(o.createdAt),
				status: o.status || 'unknown',
				paymentMethodKind: o.paymentMethodKind || '',
				returningExhibitor: o.mergedIntoExistingBusiness === true,
				isTestMode: o.isTestMode === true,
				stripeChargeId: o.stripeChargeId || '',
			};
		})
		.sort((a, b) => b.createdAtMillis - a.createdAtMillis);
}

// 6. Order status breakdown (all statuses) + failure detail.
export function statusReport(orders, dsId, ds, nowMs = Date.now()) {
	const statusMap = {};
	const reasonMap = {};
	const failed = [];
	const pending = [];
	orders.forEach((o) => {
		if (!orderMatchesDataSet(o, dsId, ds)) return;
		const status = o.status || 'unknown';
		const share = orderShare(o, dsId, ds);
		const amount = (Number(o.totalCents) || 0) * share;
		if (!statusMap[status]) statusMap[status] = { status, orders: 0, attendeeOrders: 0, exhibitorOrders: 0, totalCents: 0 };
		const s = statusMap[status];
		s.orders += 1;
		if (isExhibitorOrder(o)) s.exhibitorOrders += 1;
		else s.attendeeOrders += 1;
		s.totalCents += amount;
		const base = {
			orderId: o.orderId,
			type: isExhibitorOrder(o) ? 'Exhibitor' : 'Attendee',
			buyerName: personName(o.buyer),
			buyerEmail: (o.buyer && o.buyer.email) || '',
			company: (o.business && o.business.businessName) || (o.buyer && o.buyer.businessName) || '',
			items: itemSummary(o, dsId, ds),
			paymentMethodKind: o.paymentMethodKind || '',
			totalCents: Math.round(amount),
			createdAtMillis: toMillis(o.createdAt),
			isTestMode: o.isTestMode === true,
			stripeChargeId: o.stripeChargeId || '',
		};
		if (status === 'failed') {
			const reason = o.errorMessage || '(no reason recorded)';
			reasonMap[reason] = (reasonMap[reason] || 0) + 1;
			failed.push(Object.assign({ reason }, base));
		} else if (status === 'pending_bnpl' || status === 'pending') {
			const ageDays = base.createdAtMillis ? Math.floor((nowMs - base.createdAtMillis) / 86400000) : null;
			pending.push(Object.assign({ status, ageDays }, base));
		}
		if (status === 'partial') {
			const errs = Array.isArray(o.provisioningErrors) ? o.provisioningErrors.length : 0;
			failed.push(
				Object.assign({ reason: `Partial: charged, ${errs} attendee provisioning error(s)` }, base)
			);
		}
	});
	const order = ['paid', 'partial', 'pending_bnpl', 'pending', 'failed'];
	const statuses = Object.values(statusMap)
		.map((s) => Object.assign(s, { totalCents: Math.round(s.totalCents) }))
		.sort((a, b) => {
			const ia = order.indexOf(a.status);
			const ib = order.indexOf(b.status);
			return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || b.orders - a.orders;
		});
	const reasons = Object.keys(reasonMap)
		.map((reason) => ({ reason, orders: reasonMap[reason] }))
		.sort((a, b) => b.orders - a.orders);
	failed.sort((a, b) => b.createdAtMillis - a.createdAtMillis);
	pending.sort((a, b) => b.createdAtMillis - a.createdAtMillis);
	return { statuses, reasons, failed, pending };
}

function dataSetIdsOf(doc, ds) {
	const raw = doc && doc.dataSet;
	let ids = [];
	if (Array.isArray(raw)) ids = raw.filter((x) => x);
	else if (raw) ids = [raw];
	const missing = ids.length === 0;
	if (missing) ids = [ds.resolve(null)];
	return { ids: ids.map((x) => (x === null ? '__all__' : x)), missing };
}

// 7. App adoption: invited-but-not-signed-up (tempUsers) vs signed up (users),
// per event and role. tempUsers docs are deleted when the invitee completes
// app signup, so "pending" is literally the not-yet-converted invites.
// Staff accounts (users.isAdmin === true) are excluded.
export function adoptionRows(users, tempUsers, dsId, ds) {
	const rows = {};
	const ensure = (eventId, role) => {
		const key = `${eventId}|${role}`;
		if (!rows[key]) {
			rows[key] = {
				eventId,
				event: eventId === '__all__' ? 'All' : ds.label(eventId),
				role,
				signedUp: 0,
				pending: 0,
				pendingEmailNotSent: 0,
				noDataSetField: 0,
			};
		}
		return rows[key];
	};
	(users || []).forEach((u) => {
		if (u.isAdmin === true) return;
		const role = u.businessId ? 'Exhibitor' : 'Attendee';
		const { ids, missing } = dataSetIdsOf(u, ds);
		ids.forEach((id) => {
			if (dsId && id !== dsId) return;
			const r = ensure(id, role);
			r.signedUp += 1;
			if (missing) r.noDataSetField += 1;
		});
	});
	(tempUsers || []).forEach((t) => {
		const role = t.emailTemplate === 'vendor' || t.businessId ? 'Exhibitor' : 'Attendee';
		const { ids, missing } = dataSetIdsOf(t, ds);
		ids.forEach((id) => {
			if (dsId && id !== dsId) return;
			const r = ensure(id, role);
			r.pending += 1;
			if (t.emailSent !== true) r.pendingEmailNotSent += 1;
			if (missing) r.noDataSetField += 1;
		});
	});
	return Object.values(rows)
		.map((r) => {
			const invited = r.signedUp + r.pending;
			return Object.assign(r, {
				invited,
				adoptionPct: invited ? Math.round((r.signedUp / invited) * 1000) / 10 : 0,
			});
		})
		.sort((a, b) => a.event.localeCompare(b.event) || a.role.localeCompare(b.role));
}
